/**
 * Google Analytics 4 + Backend tracking utilities for Carted
 * Measurement ID: G-ZJ1X46G1ZE
 * Dual tracking: GA4 (client-side) + MongoDB (server-side)
 */

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Check if gtag is available
const gtag = (...args) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag(...args);
  }
};

// Send event to backend (for MongoDB storage)
const sendToBackend = async (eventType, metadata = {}) => {
  try {
    const token = localStorage.getItem('token');
    if (!token) return; // Only track authenticated users in backend
    
    await fetch(`${API_URL}/api/analytics/event`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ event_type: eventType, metadata })
    });
  } catch (error) {
    // Silently fail - don't block user actions for analytics
    console.debug('Analytics event failed:', error);
  }
};

// ==================== BUSINESS FUNNEL EVENTS ====================

/**
 * Track when user starts planning (opens meal planner with intent)
 */
export const trackPlanStarted = (pathId = null, pathName = null) => {
  gtag('event', 'plan_started', {
    event_category: 'funnel',
    path_id: pathId,
    path_name: pathName
  });
  sendToBackend('plan_started', { path_id: pathId, path_name: pathName });
};

/**
 * Track when user completes a meal plan (has 3+ meals)
 */
export const trackPlanCompleted = (mealCount, pathId = null) => {
  gtag('event', 'plan_completed', {
    event_category: 'funnel',
    meal_count: mealCount,
    path_id: pathId
  });
  sendToBackend('plan_completed', { meal_count: mealCount, path_id: pathId });
};

/**
 * Track when user builds a grocery cart
 */
export const trackCartBuilt = (itemCount, estimatedValue = 0) => {
  gtag('event', 'cart_built', {
    event_category: 'funnel',
    item_count: itemCount,
    value: estimatedValue
  });
  sendToBackend('cart_built', { item_count: itemCount, estimated_value: estimatedValue });
};

/**
 * Track checkout/export click-through
 */
export const trackCheckoutClicked = (store, itemCount) => {
  gtag('event', 'checkout_clicked', {
    event_category: 'funnel',
    store_name: store,
    item_count: itemCount
  });
  sendToBackend('checkout_clicked', { store, item_count: itemCount });
};

/**
 * Track path usage for "most used paths" analytics
 */
export const trackPathUsed = (pathId, pathName) => {
  gtag('event', 'path_used', {
    event_category: 'paths',
    path_id: pathId,
    path_name: pathName
  });
  sendToBackend('path_used', { path_id: pathId, path_name: pathName });
};

/**
 * Track meal swap for "most swapped meals" analytics
 */
export const trackMealSwapped = (fromMeal, toMeal) => {
  gtag('event', 'meal_swapped', {
    event_category: 'swaps',
    from_meal: fromMeal,
    to_meal: toMeal
  });
  sendToBackend('meal_swapped', { from_meal: fromMeal, to_meal: toMeal });
};

/**
 * Track ingredient swap for "most swapped ingredients" analytics
 */
export const trackIngredientSwapped = (fromIngredient, toIngredient) => {
  gtag('event', 'ingredient_swapped', {
    event_category: 'swaps',
    from_ingredient: fromIngredient,
    to_ingredient: toIngredient
  });
  sendToBackend('ingredient_swapped', { from_ingredient: fromIngredient, to_ingredient: toIngredient });
};

// ==================== DEMO FUNNEL EVENTS ====================

/**
 * Track when user starts the guided demo
 */
export const trackDemoStarted = (source = 'landing') => {
  gtag('event', 'demo_started', {
    event_category: 'demo_funnel',
    event_label: source,
    demo_step: 0
  });
};

/**
 * Track demo step viewed
 * @param {number} step - Step number (1-4)
 * @param {string} stepName - Name of the step
 */
export const trackDemoStepViewed = (step, stepName) => {
  gtag('event', 'demo_step_viewed', {
    event_category: 'demo_funnel',
    event_label: stepName,
    demo_step: step,
    value: step
  });
};

/**
 * Track demo step completed
 * @param {number} step - Step number (1-4)
 * @param {string} stepName - Name of the step
 * @param {object} metadata - Additional data
 */
export const trackDemoStepCompleted = (step, stepName, metadata = {}) => {
  gtag('event', 'demo_step_completed', {
    event_category: 'demo_funnel',
    event_label: stepName,
    demo_step: step,
    value: step,
    ...metadata
  });
};

/**
 * Track demo drop-off (user leaves during demo)
 * @param {number} lastStep - Last step viewed
 * @param {string} exitPage - Page where user exited
 */
export const trackDemoDropOff = (lastStep, exitPage) => {
  gtag('event', 'demo_drop_off', {
    event_category: 'demo_funnel',
    event_label: exitPage,
    demo_step: lastStep,
    value: lastStep
  });
};

// ==================== STEP 1: RECIPE SEARCH ====================

export const trackRecipeSearched = (query, isDemo = false) => {
  gtag('event', 'recipe_searched', {
    event_category: isDemo ? 'demo_funnel' : 'engagement',
    search_term: query,
    is_demo: isDemo
  });
};

export const trackRecipeViewed = (recipeId, recipeTitle, isDemo = false) => {
  gtag('event', 'recipe_viewed', {
    event_category: isDemo ? 'demo_funnel' : 'engagement',
    item_id: recipeId,
    item_name: recipeTitle,
    is_demo: isDemo
  });
};

export const trackSearchChipUsed = (chipType, isDemo = false) => {
  gtag('event', 'search_chip_used', {
    event_category: isDemo ? 'demo_funnel' : 'engagement',
    chip_type: chipType,
    is_demo: isDemo
  });
};

// ==================== STEP 2: MEAL PLANNING ====================

export const trackMealAdded = (recipeTitle, dayOfWeek, isDemo = false) => {
  gtag('event', 'meal_added', {
    event_category: isDemo ? 'demo_funnel' : 'engagement',
    item_name: recipeTitle,
    day_of_week: dayOfWeek,
    is_demo: isDemo
  });
};

export const trackMealRemoved = (recipeTitle, isDemo = false) => {
  gtag('event', 'meal_removed', {
    event_category: isDemo ? 'demo_funnel' : 'engagement',
    item_name: recipeTitle,
    is_demo: isDemo
  });
};

export const trackAutoPlanUsed = (preferences) => {
  gtag('event', 'auto_plan_used', {
    event_category: 'pro_features',
    num_meals: preferences.numMeals,
    dietary: preferences.dietary?.join(',') || 'none',
    cuisines: preferences.cuisines?.join(',') || 'none'
  });
};

// ==================== STEP 3: GROCERY LIST ====================

export const trackGroceryListGenerated = (itemCount, isDemo = false) => {
  gtag('event', 'grocery_list_generated', {
    event_category: isDemo ? 'demo_funnel' : 'engagement',
    item_count: itemCount,
    is_demo: isDemo
  });
};

export const trackGroceryItemChecked = (isDemo = false) => {
  gtag('event', 'grocery_item_checked', {
    event_category: isDemo ? 'demo_funnel' : 'engagement',
    is_demo: isDemo
  });
};

// ==================== STEP 4: EXPORT & CONVERSION ====================

export const trackExportClicked = (store, isDemo = false) => {
  gtag('event', 'export_clicked', {
    event_category: isDemo ? 'demo_funnel' : 'conversion',
    store_name: store,
    is_demo: isDemo
  });
};

export const trackExportCompleted = (store, itemCount) => {
  gtag('event', 'export_completed', {
    event_category: 'conversion',
    store_name: store,
    item_count: itemCount
  });
};

// ==================== SIGNUP & CONVERSION ====================

export const trackSignupModalShown = (trigger, demoStep = null) => {
  gtag('event', 'signup_modal_shown', {
    event_category: 'conversion',
    trigger_source: trigger,
    demo_step: demoStep
  });
};

export const trackSignupStarted = (source, demoStep = null) => {
  gtag('event', 'signup_started', {
    event_category: 'conversion',
    source: source,
    demo_step: demoStep
  });
};

export const trackSignupCompleted = (source) => {
  gtag('event', 'sign_up', {
    event_category: 'conversion',
    method: source
  });
};

export const trackUpgradeClicked = (plan, source) => {
  gtag('event', 'upgrade_clicked', {
    event_category: 'conversion',
    plan_type: plan,
    source: source
  });
};

export const trackSubscriptionStarted = (plan, price) => {
  gtag('event', 'begin_checkout', {
    event_category: 'conversion',
    currency: 'USD',
    value: price,
    items: [{ item_name: plan }]
  });
};

export const trackSubscriptionCompleted = (plan, price) => {
  gtag('event', 'purchase', {
    event_category: 'conversion',
    currency: 'USD',
    value: price,
    transaction_id: Date.now().toString(),
    items: [{ item_name: plan }]
  });
};

// ==================== PRO FEATURES ====================

export const trackProFeatureTeased = (featureName, location) => {
  gtag('event', 'pro_feature_teased', {
    event_category: 'pro_features',
    feature_name: featureName,
    location: location
  });
};

export const trackProFeatureUsed = (featureName) => {
  gtag('event', 'pro_feature_used', {
    event_category: 'pro_features',
    feature_name: featureName
  });
};

// ==================== HOUSEHOLD COLLABORATION ====================

export const trackHouseholdCreated = () => {
  gtag('event', 'household_created', {
    event_category: 'collaboration'
  });
};

export const trackHouseholdInviteSent = () => {
  gtag('event', 'household_invite_sent', {
    event_category: 'collaboration'
  });
};

export const trackHouseholdJoined = () => {
  gtag('event', 'household_joined', {
    event_category: 'collaboration'
  });
};

// ==================== PAGE VIEWS ====================

export const trackPageView = (pageName, pagePath) => {
  gtag('event', 'page_view', {
    page_title: pageName,
    page_path: pagePath
  });
};

export default {
  // Business funnel events
  trackPlanStarted,
  trackPlanCompleted,
  trackCartBuilt,
  trackCheckoutClicked,
  trackPathUsed,
  trackMealSwapped,
  trackIngredientSwapped,
  // Demo funnel events
  trackDemoStarted,
  trackDemoStepViewed,
  trackDemoStepCompleted,
  trackDemoDropOff,
  trackRecipeSearched,
  trackRecipeViewed,
  trackSearchChipUsed,
  trackMealAdded,
  trackMealRemoved,
  trackAutoPlanUsed,
  trackGroceryListGenerated,
  trackGroceryItemChecked,
  trackExportClicked,
  trackExportCompleted,
  trackSignupModalShown,
  trackSignupStarted,
  trackSignupCompleted,
  trackUpgradeClicked,
  trackSubscriptionStarted,
  trackSubscriptionCompleted,
  trackProFeatureTeased,
  trackProFeatureUsed,
  trackHouseholdCreated,
  trackHouseholdInviteSent,
  trackHouseholdJoined,
  trackPageView
};
